import { ColDef, ColGroupDef, CsvExportParams, ExcelExportParams, GridApi } from "ag-grid-community";
import { PriceGroupsStoreAddUpdate, PriceGroupsStoreView } from "models/dbModels/priceGroups/PriceGroupsStore";
import { useState } from "react";
import { Form } from "react-bootstrap";
import { Spinner } from "components";
import { useAppDispatch } from "redux/hooks";
import { modifyPriceGroupsStore, getPriceGroupsStoreSimple, updateAssignPriceGroupField } from "redux/slices/AssignPriceGroup";
import { calculateGP } from "helpers/data/dataHandling";
 
export function getPriceGroupStockConfig(assignPriceGroup: any, user: any, priceGroups: any): (ColDef<PriceGroupsStoreView> | ColGroupDef<PriceGroupsStoreView>)[] | null {        
    return [
        {
            checkboxSelection: true,
            width: 45,
            sortable: false,
            filter: false,
            suppressHeaderMenuButton: true,
        },
        {
            headerName: "Name",
            suppressHeaderMenuButton: true,
            sortable: false,
            initialSort: "asc",
            flex: 3,                    
            field: "managedStockName",
            minWidth: 200,
        },
        {
            headerName: "Cost",
            suppressHeaderMenuButton: true,
            sortable: false,
            flex: 1,
            field: "costPrice",
            cellClass: 'moneyField',
            minWidth: 80, 
            cellRendererSelector: (_) => {
                return { component: "priceCellRenderer" }
        }},
        {
            headerName: "Base Sell Price",
            suppressHeaderMenuButton: true,
            sortable: false,
            flex: 2,
            field: "basePrice",
            cellClass: 'moneyField',
            minWidth: 130,
            cellRendererSelector: (_) => {
                return { component: "priceCellRenderer" }
        }},
        {
            headerName: "Current GP%",
            suppressHeaderMenuButton: true,
            sortable: false,
            flex: 2,
            minWidth: 120,
            valueGetter: (params) => {
                const sellPrice = params.data?.currentPrice || 0;
                const costPrice = params.data?.costPrice || 0;
                const gstCode = params.data?.gstCode || 'Y';
                const cal = calculateGP(sellPrice, costPrice, gstCode);
                return cal==undefined? null: cal + "%";
            },
            cellRendererSelector: (_) => {
                return { component: "percentageCellRenderer" }
        }},
        {
            headerName: "Current Sell Price",
            suppressHeaderMenuButton: true,
            sortable: false,
            flex: 2,
            field: "currentPrice",       
            cellClass: 'moneyField',
            minWidth: 160,
            cellRendererSelector: (_) => {
                return { component: "priceCellRenderer" }
        }},
        {
            headerName: "Current Price Group",
            suppressHeaderMenuButton: true,
            sortable: false,
            width: 160,
            minWidth: 160,
            field: "priceGroupName"
        },
        {
            headerName: "New GP%", 
            suppressHeaderMenuButton: true,
            sortable: false,         
            width: 95,
            minWidth: 95,
            valueGetter: (params) => {
                const sellPrice = params.data?.newPrice || 0;
                const costPrice = params.data?.costPrice || 0;
                const gstCode = params.data?.gstCode || 'Y';
                const cal =calculateGP(sellPrice, costPrice, gstCode);
                return cal==undefined? null: cal + "%";
            },
            cellRendererSelector: (_) => {
                return { component: "percentageCellRenderer" }
            }
        },
        {
            headerName: "New Sell Price",
            suppressHeaderMenuButton: true,
            sortable: false,
            field: "newPrice",
            flex: 2,
            cellClass: 'moneyField',
            minWidth: 135,
            cellRendererSelector: (_) => {
                return { component: "priceCellRenderer" }
            }
        },
        {
            headerName: "New Price Group",
            suppressHeaderMenuButton: true,
            sortable: false,
            flex: 3,
            minWidth: 180,
            field: "newPriceGroupId",           
            cellRendererSelector: (params) => {
                return {
                    component: 'baseCellRenderer',
                    params: {
                                children: <PriceGroupDropdown aggridApi={params.api} assignPriceGroup={assignPriceGroup} data={params.data as PriceGroupsStoreView} priceGroups={priceGroups} user={user}/>,
                            }                   
                }
            }
        }
    ];
}

type priceGroupDropdownProps = {aggridApi: GridApi,data: PriceGroupsStoreView, assignPriceGroup: any, user: any, priceGroups: any}
const PriceGroupDropdown = ({aggridApi, data, assignPriceGroup, user, priceGroups}: priceGroupDropdownProps) => {
    const dispatch = useAppDispatch(); 
        const [priceGroup, setPriceGroup] = useState(data.newPriceGroupId ? data.newPriceGroupId: "Mixed");
        const [loadingValue, setLoadingValue] = useState(false);
        async function handlePriceGroupUpdate(priceGroupId: number, managedStockId: number[]) {
            setLoadingValue(true);
        let payload: PriceGroupsStoreAddUpdate[] = [];
      
            payload.push({
                storeId: assignPriceGroup.currentStore,
                managedStockId: data.managedStockId,
                newPriceGroupId: priceGroupId
            });
            await dispatch(modifyPriceGroupsStore(payload)).then((res) => {
                if (res.data) {
                    setPriceGroup(priceGroupId);
                    dispatch(getPriceGroupsStoreSimple({
                        groupBy: "None",
                        storeIds: [assignPriceGroup.currentStore],
                        managedStockIds: [data.managedStockId]
                    })).then(res => {
                        let stocks: any[] = [...assignPriceGroup.stocks];
                        res.data.forEach((item: any) => {
                            const index = stocks.findIndex((stock: any) => stock.managedStockId === item.managedStockId);
                            if (index >= 0){
                                stocks[index] = {...item, id: item.managedStockId };
                                data.newPrice = item.newPrice;
                                data.currentPrice = item.currentSellPrice;                                
                            }                         
                        })
                        dispatch(updateAssignPriceGroupField({ key: "stocks", value: stocks }));
                           
                    })
                }
            });
            setLoadingValue(false);   
    }
    return (
        <div style={{ maxWidth: "250px", display: "flex" }}>
            {loadingValue ?
                <span><Spinner className="spinner-border-sm me-1" tag="span" color="primary" />Saving... </span>
                    :
                <Form.Group style={{ margin: "-3px 0px" }}>
                    <Form.Select
                        disabled={!user.permissions.assignPriceGroups.canUpdate}
                        className="custom-select"
                        value={priceGroup}
                        onChange={(e) => handlePriceGroupUpdate(parseInt(e.target.value), [data.managedStockId])}
                    >
                        <option value={0}></option>
                        <option value={-1}>Base Sell Price</option>
                        <option value={-2} hidden>Mixed</option>
                        {priceGroups.table.map((priceLevel: any, index: any) => {
                            return <option key={index} value={priceLevel.id}>{priceLevel.name}</option>
                        })}
                    </Form.Select>
                </Form.Group>
                }
        </div>
    );
};

export function getPriceGroupStockCsvDataFormatter(): CsvExportParams{
    return {
        fileName: "PriceGroupStock" + new Date().toLocaleDateString('en-AU', {
            day : 'numeric',
            month : 'short',
            year : 'numeric',
            hour : '2-digit',
            minute : '2-digit'
        }).split(' ').join('-').replace(',', '') +".csv",

        processCellCallback: (cell) => {
            if (["3","4","5"].includes(cell.column.getColId()) && cell.value != null) {
             
                return `'${cell.value}` + "%";
              }
              if (cell.column.getColId() === 'applied' && cell.value != null) {
                return (cell.value as String).split(' ')[0];
              }
              return cell.value;
        },
    }
}

export function getPriceGroupStockDataFormatter(): ExcelExportParams {
    return {
        processCellCallback: (cell) => {
            if (["3","4","5"].includes(cell.column.getColId()) && cell.value != null) {             
                return `'${cell.value}` + "%";
              }
              if (cell.column.getColId() === 'applied' && cell.value != null) {
                return (cell.value as String).split(' ')[0];
              }
              return cell.value;
        },
        sheetName: "Price Group"
    }
}